import { Box, Button, FormControl, Grid, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import esLocale from 'date-fns/locale/es';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../actions';
import moment from 'moment';
import { typeByName } from '../../../constants/Filters';
import axios from 'axios';
import { useFirestore } from 'react-redux-firebase';
import collections from '../../../constants/FirestoreCollections';
import { handleErrorSwal, handleWarningSwal } from '../../../shared/swals';

const useStyles = makeStyles((theme) => ({
  formControl: {
    paddingBlock: 16,
  },
}));

function StopProcessService() {
  const organization = useSelector((state) => state.app.organization);
  const projectId = process.env.REACT_APP_PROJECT_ID;
  const profile = useSelector((state) => state.firebase.profile);
  const [config, setConfig] = useState({
    botsStopped: [],
    stopProcess: {},
  });
  const firestore = useFirestore();

  useEffect(() => {
    if (organization.id) {
      firestore
        .collection(collections.configurations)
        .where('organizationId', '==', organization.id)
        .where('name', '==', 'botsStopped')
        .get()
        .then((res) => {
          if (res.empty) {
            console.error('ScheduledMessageService: No se encontró la configuración botsStopped');
            handleErrorSwal();
          } else {
            setConfig({
              ...organization.stopProcess,
              configId: res.docs[0].id,
              botsStopped: res.docs[0].data().stopProcess || [],
            });
          }
        });
    }
  }, [firestore, organization.id, organization.stopProcess]);

  useEffect(() => {
    if (config.configId) {
      const unsubscribe = firestore
        .collection(collections.configurations)
        .doc(config.configId)
        .onSnapshot((snapshot) => {
          setConfig((previousConfig) => ({
            ...previousConfig,
            botsStopped: snapshot.data().stopProcess,
          }));
        });
      return () => {
        unsubscribe && unsubscribe();
      };
    }
  }, [config.id]);

  const stopBots = async (initialMessage, date) => {
    const result = await firestore
      .collection(collections.chats)
      .where('updatedAt', '>=', date.toDate())
      .where('botStopped', '==', false)
      .where('status', 'in', [0, 2])
      .where('organizationId', '==', organization.id)
      .get();

    const chats = result.docs.map((snapshot) => ({ id: snapshot.id, ...snapshot.data() }));

    // stop bot and send initial message in the background
    chats.map((chat) => {
      firestore
        .collection(collections.chats)
        .doc(chat.id)
        .update({
          botStopped: true,
          stoppedHistory: firestore.FieldValue.arrayUnion({
            user: profile.email,
            date: new Date(Date.now()),
          }),
        });
      // send initial message to
      handleSendMessage(chat, initialMessage);
    });

    // update configurations
    await firestore
      .collection(collections.organizations)
      .doc(organization.id)
      .update({
        stopProcess: {
          running: true,
          initialMessage,
          hour: date.toDate(),
        },
      });
    await firestore
      .collection(collections.configurations)
      .doc(config.configId)
      .update({
        stopProcess: chats.map((e) => e.id) || [],
      });
  };

  const activateBots = async (endMessage) => {
    // Obtener configuración para obtener los chats detenidos actualizados
    const configRef = firestore.collection(collections.configurations).doc(config.configId);
    const configSnap = await configRef.get();
    if (configSnap.empty) {
      console.error('activateBots: No se encontró la configuración botsStopped');
      handleErrorSwal();
      return;
    }

    const botsStoppedConfig = { id: configSnap.id, ...configSnap.data() };
    const { stopProcess = [] } = botsStoppedConfig;

    if (!stopProcess.length) {
      // update configurations
      await firestore
        .collection(collections.organizations)
        .doc(organization.id)
        .update({
          stopProcess: { running: false },
        });
      handleWarningSwal('No hay chats detenidos');
      return;
    }
    const chats = (
      await Promise.all(
        stopProcess.map((chatId) => firestore.collection(collections.chats).doc(chatId).get()),
      )
    ).map((snapshot) => ({ id: snapshot.id, ...snapshot.data() }));
    // activate chat and send end message in the background
    stopProcess.map((id) => {
      firestore
        .collection(collections.chats)
        .doc(id)
        .update({ botStopped: false })
        .then(() => {
          // send finish message to
          const chat = chats.find((e) => e.id === id);
          handleSendMessage(chat, endMessage);
        });
    });

    // update configurations
    await firestore
      .collection(collections.organizations)
      .doc(organization.id)
      .update({
        stopProcess: { running: false },
      });
    await firestore.collection(collections.configurations).doc(config.configId).update({
      stopProcess: [],
    });
  };

  const handleSendMessage = (chat, messageText) => {
    const message = {
      creationDate: new Date(),
      contentType: 'chat',
      type: 1,
      email: profile.email,
      message: messageText,
    };
    if (chat.type === typeByName.whatsapp) {
      return axios({
        url: `https://us-central1-${projectId}.cloudfunctions.net/gateway`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'post',
        data: {
          to: chat.client.phone,
          provider: organization.provider,
          message: messageText,
          organizationId: organization.id,
          email: profile.email,
        },
      });
    } else {
      return firestore
        .collection(collections.chats)
        .doc(chat.id)
        .update({ messages: firestore.FieldValue.arrayUnion(message) });
    }
  };

  return { config, stopBots, activateBots };
}

export default function StopProcess(props) {
  const classes = useStyles();
  // hooks
  const { config, activateBots, stopBots } = StopProcessService();
  // redux
  const appDispatch = useDispatch();
  // states
  const [initialMessage, setInitialMessage] = useState('');
  const [endMessage, setEndMessage] = useState('');
  const [date, setDate] = useState(moment());

  useEffect(() => {
    // update fields
    if (config.hour && config.hour.seconds) {
      setDate(moment(new Date(config.hour.seconds * 1000)));
    }
    if (config.initialMessage) {
      setInitialMessage(config.initialMessage);
    }
  }, [config]);

  const handleChangeProcess = async () => {
    appDispatch({
      type: actions.changeLoading,
      loading: { loading: true, priority: 1 },
    });
    try {
      if (config && config.running) {
        await activateBots(endMessage);

        // reset inputs
        setEndMessage('');
        setInitialMessage('');
        setDate(moment());

        // show message
        appDispatch({
          type: actions.alert,
          alert: {
            snackbarOpen: true,
            snackbarMessage: `Bots activados exitosamente!`,
            snackbarSeverity: 'success',
          },
        });
      } else {
        await stopBots(initialMessage, date);
        // show message
        appDispatch({
          type: actions.alert,
          alert: {
            snackbarOpen: true,
            snackbarMessage: `Bots frenados exitosamente!`,
            snackbarSeverity: 'success',
          },
        });
      }
    } finally {
      // hide loader
      appDispatch({
        type: actions.changeLoading,
        loading: { loading: false, priority: 1 },
      });
    }
  };

  const handleChangeDate = (date) => {
    const target = new Date(date);
    const now = new Date(Date.now());
    let result;
    if (
      target.getHours() < now.getHours() ||
      (target.getHours() === now.getHours() && target.getMinutes() < now.getMinutes())
    ) {
      result = moment(new Date(date));
    } else {
      const yesterday = moment().subtract(1, 'day');
      yesterday.minutes(target.getMinutes());
      yesterday.hours(target.getHours());
      result = yesterday;
    }
    setDate(result);
  };

  return (
    <>
      <Typography>
        <b>PROCESO DE DETENCIÓN</b>
      </Typography>
      <Grid container>
        <Grid xs={6} sx={{ pr: 4, display: 'flex', flexDirection: 'column' }}>
          <Box py={2}>
            <Typography>¿CÓMO DETENER CHATS?</Typography>
            <Typography>
              1) Se frenaran todos los bots desde la hora indicada y todos los chats que vayan
              entrando
            </Typography>
            <Typography>
              2) A los chats mencionados tambien se les enviara el 'mensaje de inicio'
            </Typography>
          </Box>
          <Grid item xs={12}>
            <FormControl className={classes.formControl} fullWidth>
              <span>Mensaje de inicio</span>
              <TextField
                id="outlined-multiline-static"
                multiline
                value={initialMessage}
                onChange={(e) => setInitialMessage(e.target.value)}
                rows={6}
                disabled={config && config.running}
                variant="outlined"
                spellCheck="false"
                placeholder="Escriba un mensaje"
              />
            </FormControl>
          </Grid>
          <LocalizationProvider adapterLocale={esLocale} dateAdapter={AdapterDateFns}>
            <Box mt={1}>
              <DesktopTimePicker
                label="¿Desde que hora?"
                inputFormat="HH:mm"
                value={date}
                onChange={handleChangeDate}
                renderInput={(params) => <TextField {...params} />}
                disabled={config && config.running}
              />
            </Box>
          </LocalizationProvider>
          <FormControl className={classes.formControl}>
            <Button
              disabled={config && config.running}
              variant="outlined"
              color="primary"
              onClick={handleChangeProcess}
            >
              INICIAR PROCESO
            </Button>
          </FormControl>
        </Grid>
        <Grid xs={6} sx={{ pl: 4, display: 'flex', flexDirection: 'column' }}>
          <Box py={2}>
            <Typography>¿CÓMO LIBERAR LOS CHATS DETENIDOS?</Typography>
            <Typography>
              1) Se activaran todos los chats que fueron frenados en "cómo detener chats"
            </Typography>
            <Typography>2) Se les enviara el 'mensaje de fin'</Typography>
          </Box>
          <Grid item xs={12}>
            <FormControl className={classes.formControl} fullWidth>
              <span>Mensaje de fin</span>
              <TextField
                id="outlined-multiline-static"
                multiline
                value={endMessage}
                onChange={(e) => setEndMessage(e.target.value)}
                rows={6}
                variant="outlined"
                spellCheck="false"
                placeholder="Escriba un mensaje"
                disabled={!config || !config.running}
              />
            </FormControl>
          </Grid>
          <FormControl className={classes.formControl}>
            <Button
              disabled={!config || !config.running}
              variant="outlined"
              color="primary"
              onClick={handleChangeProcess}
            >
              FINALIZAR PROCESO
            </Button>
          </FormControl>
        </Grid>
      </Grid>
    </>
  );
}
